// src/pages/stylist/ServiceManagementPage.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../../utils/formatters';
import { useAppDispatch, useAppSelector } from '../../hooks/useRedux';
import { useUI } from '../../context/UIContext';
import { Service, ServiceFormData } from '../../types/service';
import {
  fetchStylistServices,
  createService,
  updateService,
  deleteService
} from '../../store/slices/servicesSlice';
import ServiceEditorForm from '../../components/stylist/ServiceEditorForm';
import '../../styles/ServiceManagement.css';

// Extend Service type with extra optional props for grouping
interface ExtendedService extends Service {
  subcategory: string;
  source?: string;
  salon?: any;
}

// Create an extended type for the ServiceEditorForm initial data.
export interface ExtendedServiceFormData extends ServiceFormData {
  subcategory: string;
}

const ServiceManagementPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { showSnackbar } = useUI();
  const { services, isLoading, error } = useAppSelector((state) => state.services);
  const { user } = useAppSelector((state) => state.auth);

  // Local state for the editor modal (used for edits) and search input.
  const [editorOpen, setEditorOpen] = useState<boolean>(false);
  const [editingService, setEditingService] = useState<ExtendedService | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [serviceToDelete, setServiceToDelete] = useState<ExtendedService | null>(null);
  
  // Debug state changes
  useEffect(() => {
    console.log('State changed - editorOpen:', editorOpen, 'editingService:', editingService);
  }, [editorOpen, editingService]);
  
  useEffect(() => {
    console.log('State changed - deleteDialogOpen:', deleteDialogOpen, 'serviceToDelete:', serviceToDelete);
  }, [deleteDialogOpen, serviceToDelete]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [formError, setFormError] = useState<string | null>(null);

  const dialogRef = useRef<HTMLDivElement>(null);
  const deleteDialogRef = useRef<HTMLDivElement>(null);

  // Log authentication state for debugging.
  useEffect(() => {
    console.log("Auth state in ServiceManagementPage:", {
      user,
      token: localStorage.getItem('token'),
      isAuthenticated: !!user && !!localStorage.getItem('token')
    });
  }, [user]);

  // On mount, fetch stylist services using user.id.
  useEffect(() => {
    if (user && user.id) {
      console.log("User found, fetching services with id:", user.id);
      dispatch(fetchStylistServices(user.id));
    } else {
      console.warn("User id missing, cannot fetch services");
    }
  }, [user, dispatch]); // Include dispatch

  // Close modals on Escape key press.
  useEffect(() => {
    const handleEscKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (editorOpen) handleEditorClose();
        if (deleteDialogOpen) handleDeleteDialogClose();
      }
    };
    document.addEventListener('keydown', handleEscKeyPress);
    return () => document.removeEventListener('keydown', handleEscKeyPress);
  }, [editorOpen, deleteDialogOpen]);

  // Close modals on outside clicks.
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (editorOpen && dialogRef.current && !dialogRef.current.contains(e.target as Node)) {
        handleEditorClose();
      }
      if (deleteDialogOpen && deleteDialogRef.current && !deleteDialogRef.current.contains(e.target as Node)) {
        handleDeleteDialogClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [editorOpen, deleteDialogOpen]);

  // Group services into "Your Services" and "Assigned Services".
  const groupServices = () => {
    const yourServices = services.filter((service) => {
      const s = service as ExtendedService;
      return !s.salon || s.source === 'stylist';
    });
    const assignedServices = services.filter((service) => {
      const s = service as ExtendedService;
      return Boolean(s.salon) || s.source === 'manager';
    });
    return { yourServices, assignedServices };
  };

  const { yourServices, assignedServices } = groupServices();

  // Filter services by search query.
  const filterServices = (servicesList: Service[]) => {
    if (!searchQuery.trim()) return servicesList;
    const query = searchQuery.toLowerCase();
    return servicesList.filter(service =>
      service.name.toLowerCase().includes(query) ||
      (service.description?.short && service.description.short.toLowerCase().includes(query))
    );
  };

  const filteredYourServices = filterServices(yourServices);
  const filteredAssignedServices = filterServices(assignedServices);

  // Updated "Add New Service" handler: Instead of opening the blank ServiceEditorForm modal,
  // we navigate to the Service Template Page so the stylist can pick a template—mimicking mobile.
  const handleAddService = () => {
    console.log("Navigating to Service Template Page for new service creation");
    navigate('/stylist/service-template');
  };
  
  // Navigate to Add-On Template Page
  const handleManageAddOns = () => {
    console.log("Navigating to Add-On Template Page");
    navigate('/stylist/addon-template');
  };
  
  // Prepare a link to add a new service via template for the empty state
  const handleAddFromTemplate = () => {
    navigate('/stylist/service-template');
  };

  const handleEditService = (service: ExtendedService) => {
    console.log('Edit service clicked:', service);
    console.log('Setting editingService and editorOpen to true');
    setEditingService(service);
    setEditorOpen(true);
    console.log('After setState - editorOpen should be true');
  };

  const handleDeleteClick = (service: ExtendedService) => {
    console.log('Delete service clicked:', service);
    setServiceToDelete(service);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    console.log('Delete confirm clicked, serviceToDelete:', serviceToDelete);
    if (!serviceToDelete) {
      console.error('No service to delete');
      showSnackbar('Unable to delete: No service selected', 'error');
      setDeleteDialogOpen(false);
      return;
    }
    
    const serviceName = serviceToDelete.name;
    const serviceId = serviceToDelete._id;
    
    if (!serviceId) {
      showSnackbar('Unable to delete: Invalid service ID', 'error');
      setDeleteDialogOpen(false);
      setServiceToDelete(null);
      return;
    }
    
    console.log('Dispatching deleteService for:', serviceId);
    dispatch(deleteService(serviceId))
      .unwrap()
      .then(() => {
        console.log('Delete successful');
        showSnackbar(`Service "${serviceName}" deleted successfully`, 'success');
        // Refresh services list
        if (user?.id) {
          dispatch(fetchStylistServices(user.id));
        }
      })
      .catch((err: any) => {
        const errorMessage = typeof err === 'string' 
          ? err 
          : err.message || err.error || 'Failed to delete service. Please try again later.';
        console.error('Delete failed:', err);
        showSnackbar(errorMessage, 'error');
      });
    setDeleteDialogOpen(false);
    setServiceToDelete(null);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setServiceToDelete(null);
  };

  const handleEditorClose = () => {
    setEditorOpen(false);
    setEditingService(null);
  };

  // When ServiceEditorForm submits, either update an existing service or create a new one.
  const handleServiceSubmit = (data: ServiceFormData) => {
    setFormError(null);
    
    // Validate required fields
    if (!data.name || !data.name.trim()) {
      const error = 'Service name is required';
      setFormError(error);
      showSnackbar(error, 'error');
      return;
    }
    
    if (!data.description?.short || !data.description.short.trim()) {
      const error = 'Service description is required';
      setFormError(error);
      showSnackbar(error, 'error');
      return;
    }
    
    if (!data.basePrice || data.basePrice.min <= 0) {
      const error = 'Service price must be greater than $0';
      setFormError(error);
      showSnackbar(error, 'error');
      return;
    }
    
    if (!data.baseDuration || data.baseDuration.min <= 0) {
      const error = 'Service duration must be greater than 0 minutes';
      setFormError(error);
      showSnackbar(error, 'error');
      return;
    }
    
    if (data.basePrice.max < data.basePrice.min) {
      const error = 'Maximum price must be greater than or equal to minimum price';
      setFormError(error);
      showSnackbar(error, 'error');
      return;
    }
    
    if (data.baseDuration.max < data.baseDuration.min) {
      const error = 'Maximum duration must be greater than or equal to minimum duration';
      setFormError(error);
      showSnackbar(error, 'error');
      return;
    }
    
    if (editingService) {
      dispatch(updateService({ serviceId: editingService._id, serviceData: data }))
        .unwrap()
        .then(() => {
          showSnackbar(`Service "${data.name}" updated successfully`, 'success');
          setEditorOpen(false);
          setEditingService(null);
          setFormError(null);
          // Refresh services list
          if (user?.id) {
            dispatch(fetchStylistServices(user.id));
          }
        })
        .catch((err: any) => {
          const errorMessage = typeof err === 'string'
            ? err
            : err?.message || err?.error || 'We couldn’t save your changes. Please check the form and try again.';
          setFormError(errorMessage);
          showSnackbar(errorMessage, 'error');
        });
    } else {
      // If new service creation initiated elsewhere (via template flow), this branch may not run.
      if (user && user.userType === 'stylist') {
        dispatch(createService({ stylistId: user.id, serviceData: data }))
          .unwrap()
          .then(() => {
            showSnackbar(`Service "${data.name}" created successfully`, 'success');
            setEditorOpen(false);
            setEditingService(null);
            setFormError(null);
            // Refresh services list
            if (user?.id) {
              dispatch(fetchStylistServices(user.id));
            }
          })
          .catch((err: any) => {
            const errorMessage = typeof err === 'string'
              ? err
              : err?.message || err?.error || 'We couldn’t create the service. Please check the form and try again.';
            setFormError(errorMessage);
            showSnackbar(errorMessage, 'error');
          });
      } else {
        dispatch(createService({ stylistId: user ? user.id : '', serviceData: data }))
          .unwrap()
          .then(() => {
            showSnackbar(`Service "${data.name}" created successfully`, 'success');
            setEditorOpen(false);
            setEditingService(null);
            setFormError(null);
            // Refresh services list
            if (user?.id) {
              dispatch(fetchStylistServices(user.id));
            }
          })
          .catch((err: any) => {
            const errorMessage = typeof err === 'string'
              ? err
              : err?.message || err?.error || 'We couldn’t create the service. Please check the form and try again.';
            setFormError(errorMessage);
            showSnackbar(errorMessage, 'error');
          });
      }
    }
  };

  // Build consistent initial form data from the same service object shown on the card (so edit matches what user saw)
  const getEditInitialData = (service: ExtendedService): ExtendedServiceFormData => {
    const desc = service.description;
    const description = typeof desc === 'string'
      ? { short: desc, detailed: desc }
      : {
          short: desc?.short ?? '',
          detailed: (desc as { detailed?: string })?.detailed ?? (desc as { full?: string })?.full ?? desc?.short ?? ''
        };
    return {
      name: service.name ?? '',
      description,
      category: service.category ?? '',
      subcategory: (service as ExtendedService).subcategory ?? service.category ?? '',
      basePrice: service.basePrice ?? { min: 0, max: 0 },
      baseDuration: service.baseDuration ?? { min: 30, max: 30 },
      variations: Array.isArray(service.variations) ? service.variations : [],
      isActive: service.isActive ?? true
    };
  };

  // Render a service card.
  const renderServiceCard = (serviceItem: ExtendedService, isAssigned: boolean) => {
    return (
      <div key={serviceItem._id} className={`service-card ${!serviceItem.isActive ? 'service-card--inactive' : ''}`}>
        <div className="service-card__content">
          <h3 className="service-card__title">{serviceItem.name}</h3>
          <p className="service-card__description">
            {typeof serviceItem.description === 'string'
              ? serviceItem.description
              : serviceItem.description?.short || 'No description'}
          </p>
          
          <div className="service-card__details">
            <div className="service-card__detail">
              <span className="service-card__detail-label">Price</span>
              <span className="service-card__detail-value">
                {serviceItem.basePrice?.min === serviceItem.basePrice?.max
                  ? formatCurrency(serviceItem.basePrice?.min || 0)
                  : `${formatCurrency(serviceItem.basePrice?.min || 0)} - ${formatCurrency(serviceItem.basePrice?.max || 0)}`}
              </span>
            </div>
            
            <div className="service-card__detail">
              <span className="service-card__detail-label">Duration</span>
              <span className="service-card__detail-value">
                {serviceItem.baseDuration?.min === serviceItem.baseDuration?.max
                  ? `${serviceItem.baseDuration?.min || 30} min`
                  : `${serviceItem.baseDuration?.min || 30}-${serviceItem.baseDuration?.max || 60} min`}
              </span>
            </div>
          </div>
          
          {serviceItem.variations && (
            (() => {
              // Handle variations - can be array or object
              let variationsCount = 0;
              let variationsList: any[] = [];
              
              if (Array.isArray(serviceItem.variations)) {
                variationsCount = serviceItem.variations.length;
                variationsList = serviceItem.variations;
              } else if (typeof serviceItem.variations === 'object') {
                // Count all variations from hair, size, complexity
                const variationsObj = serviceItem.variations as any;
                const hairCount = variationsObj.hair?.length || 0;
                const sizeCount = variationsObj.size?.length || 0;
                const complexityCount = variationsObj.complexity?.length || 0;
                variationsCount = hairCount + sizeCount + complexityCount;
                
                // Create a flat list for display
                if (hairCount > 0) variationsList.push(...variationsObj.hair);
                if (sizeCount > 0) variationsList.push(...variationsObj.size);
                if (complexityCount > 0) variationsList.push(...variationsObj.complexity);
              }
              
              return variationsCount > 0 ? (
                <div className="service-card__variations">
                  <div className="service-card__variations-header">
                    <span className="service-card__variations-label">Variations</span>
                    <span className="service-card__variations-count">{variationsCount}</span>
                  </div>
                  <div className="service-card__tags">
                    {variationsList.slice(0, 3).map((variation, idx) => (
                      <span key={variation.id || variation._id || idx} className="service-card__tag">
                        {variation.name || variation.length || variation.size || variation.level || 'Variation'}
                      </span>
                    ))}
                    {variationsCount > 3 && (
                      <span className="service-card__tag service-card__tag--more">+{variationsCount - 3}</span>
                    )}
                  </div>
                </div>
              ) : null;
            })()
          )}
        </div>
        
        <div className="service-card__actions">
          <button
            className="service-card__action-btn service-card__action-btn--edit"
            onClick={() => handleEditService(serviceItem)}
            aria-label="Edit service"
          >
            Edit
          </button>
          {!isAssigned && (
            <button
              className="service-card__action-btn service-card__action-btn--delete"
              onClick={() => handleDeleteClick(serviceItem)}
              aria-label="Delete service"
            >
              Delete
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="service-management">
      {/* Header - Tesla Style */}
      <div className="service-management__header">
        <h1 className="service-management__title">Service Management</h1>
        <p className="service-management__subtitle">Manage your salon services, pricing, and availability.</p>
      </div>
      
      {/* Action Toolbar */}
      <div className="service-management__toolbar">
        <button
          className="service-management__add-btn"
          onClick={handleAddService}
          disabled={isLoading}
        >
          Browse Service Templates
        </button>
        <button
          className="service-management__addon-btn"
          onClick={handleManageAddOns}
          disabled={isLoading}
        >
          Manage Add-Ons
        </button>
        
        <div className="service-management__search">
          <input
            type="text"
            placeholder="Search services..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="service-management__search-input"
          />
          <div className="service-management__search-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z" fill="currentColor"/>
            </svg>
          </div>
        </div>
      </div>
      
      {/* Loading, Error and Empty States */}
      {isLoading && services.length === 0 ? (
        <div className="service-management__loading">
          <div className="service-management__loader"></div>
          <p>Loading your services...</p>
        </div>
      ) : error ? (
        <div className="service-management__error">
          <div className="service-management__error-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 22C6.477 22 2 17.523 2 12C2 6.477 6.477 2 12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22ZM12 20C16.418 20 20 16.418 20 12C20 7.582 16.418 4 12 4C7.582 4 4 7.582 4 12C4 16.418 7.582 20 12 20ZM11 15H13V17H11V15ZM11 7H13V13H11V7Z" fill="currentColor"/>
            </svg>
          </div>
          <h3>Error Loading Services</h3>
          <p>{typeof error === 'string' ? error : error?.message || 'Failed to load services'}</p>
          <button
            className="service-management__retry-btn"
            onClick={() => user?.id && dispatch(fetchStylistServices(user.id))}
          >
            Retry
          </button>
        </div>
      ) : services.length === 0 ? (
        <div className="service-management__empty">
          <div className="service-management__empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 5V19H5V5H19ZM21 3H3V21H21V3ZM13 7H11V9H9V11H11V13H13V11H15V9H13V7Z" fill="currentColor"/>
            </svg>
          </div>
          <h2>No Services Added Yet</h2>
          <p>Add your first service to start accepting appointments.</p>
          <button 
            className="service-management__add-empty-btn"
            onClick={handleAddFromTemplate}
          >
            Browse Service Templates
          </button>
        </div>
      ) : (
        <>
          {/* Your Services Section */}
          {filteredYourServices.length > 0 && (
            <div className="service-management__section">
              <h2 className="service-management__section-title">Your Services</h2>
              <div className="service-management__grid">
                {filteredYourServices.map((service) => renderServiceCard(service as ExtendedService, false))}
              </div>
            </div>
          )}
          
          {/* Assigned Services Section */}
          {filteredAssignedServices.length > 0 && (
            <div className="service-management__section">
              <h2 className="service-management__section-title">Assigned Services</h2>
              <div className="service-management__grid">
                {filteredAssignedServices.map((service) => renderServiceCard(service as ExtendedService, true))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Service Editor Modal */}
      {editorOpen && (
        <div className="tesla-modal active">
          <div className="tesla-modal__backdrop" onClick={handleEditorClose}></div>
          <div className="tesla-modal__container" ref={dialogRef}>
            <div className="tesla-modal__header">
              <h2 className="tesla-modal__title">
                {editingService ? `Edit Service: ${editingService.name}` : 'Add New Service'}
              </h2>
              <button
                className="tesla-modal__close"
                onClick={handleEditorClose}
                aria-label="Close dialog"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="currentColor"/>
                </svg>
              </button>
            </div>
            
            <div className="tesla-modal__content">
              {formError && (
                <div className="tesla-alert tesla-alert--error">
                  <p>{formError}</p>
                </div>
              )}
              
              {editingService ? (
                <ServiceEditorForm
                  key={editingService._id}
                  initialData={getEditInitialData(editingService)}
                  onSubmit={handleServiceSubmit}
                  onCancel={handleEditorClose}
                  isLoading={isLoading}
                />
              ) : (
                <ServiceEditorForm
                  key="new-service"
                  onSubmit={handleServiceSubmit}
                  onCancel={handleEditorClose}
                  isLoading={isLoading}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteDialogOpen && (
        <div className="tesla-modal active">
          <div className="tesla-modal__backdrop" onClick={handleDeleteDialogClose}></div>
          <div className="tesla-modal__container tesla-modal__container--sm" ref={deleteDialogRef}>
            <div className="tesla-modal__header">
              <h2 className="tesla-modal__title">Confirm Delete: {serviceToDelete?.name}</h2>
              <button
                className="tesla-modal__close"
                onClick={handleDeleteDialogClose}
                aria-label="Close dialog"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="currentColor"/>
                </svg>
              </button>
            </div>
            
            <div className="tesla-modal__content">
              <div className="tesla-alert tesla-alert--warning">
                <p>
                  Are you sure you want to delete "{serviceToDelete?.name}"? This action cannot be undone.
                </p>
              </div>
            </div>
            
            <div className="tesla-modal__actions">
              <button
                className="tesla-button tesla-button--secondary"
                onClick={handleDeleteDialogClose}
              >
                Cancel
              </button>
              <button
                className="tesla-button tesla-button--danger"
                onClick={handleDeleteConfirm}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceManagementPage;